import { Box, InputLabel } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import React from "react";
import { Control, useController } from "react-hook-form";

interface Option {
  value: string;
  label: string;
}

interface AGSearchableSelectProps {
  options: Option[];
  control: Control<any>;
  name: string;
  label: string;
  size?: "small" | "medium";
  required?: boolean;
  onChangeValue?: (value: string | string[]) => void;
  multiple?: boolean;
}

const AGSearchableSelect: React.FC<AGSearchableSelectProps> = ({
  options,
  control,
  name,
  label,
  size = "small",
  required = false,
  onChangeValue,
  multiple = false,
}) => {
  const {
    field: { value, onChange, onBlur },
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: multiple ? [] : "",
    rules: {
      validate: (val) => {
        if (required) {
          if (multiple) {
            return Array.isArray(val) && val.length > 0
              ? true
              : `${label} is required`;
          }
          return val ? true : `${label} is required`;
        }
        return true;
      },
    },
  });

  const selectedValue = multiple
    ? options.filter((opt) => Array.isArray(value) && value.includes(opt.value))
    : options.find((opt) => opt.value === value) || null;

  const handleChange = (
    _: React.SyntheticEvent,
    newValue: Option | Option[] | null
  ) => {
    if (multiple) {
      const values = Array.isArray(newValue)
        ? newValue.map((opt) => opt.value)
        : [];
      onChange(values);
      onChangeValue?.(values);
    } else {
      const selected = newValue as Option | null;
      const newVal = selected ? selected.value : "";
      onChange(newVal);
      onChangeValue?.(newVal);
    }
  };

  return (
    <Box sx={{ mb: 1 }}>
      <InputLabel className="mb-1 font-semibold text-black text-sm">
        {label} {required ? "*" : ""}
      </InputLabel>
      <Autocomplete
        options={options}
        getOptionLabel={(option) => option.label}
        value={selectedValue}
        onChange={handleChange}
        onBlur={onBlur}
        size={size}
        multiple={multiple}
        disableClearable={!multiple}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            fullWidth
            error={!!error}
            helperText={error?.message}
          />
        )}
      />
    </Box>
  );
};

export default AGSearchableSelect;
